import doNothing from '@opsbase/lib-general/utils/doNothing';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useSelfActions from '@opsbase/react-ctrl-self/useSelfActions';
import useOrgFindById from '@opsbase/react-gql-hooks/organisation/useOrgFindById';
import useOrgUpdate from '@opsbase/react-gql-hooks/organisation/useOrgUpdate';
import AppPage from '@opsbase/web-app/AppPage';
import useCss from '@opsbase/web-css';
import ImageEditorDialog from '@opsbase/web-image/ImageEditorDialog/ImageEditorDialog';
import ImageUploadButton from '@opsbase/web-image/ImageUploadButton/ImageUploadButton';
import ImageViewer from '@opsbase/web-image/ImageViewer/ImageViewer';
import useManyImages from '@opsbase/web-image/useManyImages';
import Card from '@opsbase/web-layout/Card/Card';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import Button from '@opsbase/web-ui/Button';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useState } from 'react';
import { SketchPicker } from 'react-color';
import opsbaseLogo from '../../shared/assets/images/logo-only.svg';
const makeMyClasses = titleColor => ({
    root: {
        backgroundColor: Palette.White.main,
    },
    container: {
        minHeight: 32,
    },
    panetext: {
        minWidth: 100,
    },
    panetextfield: {
        paddingLeft: 8,
        width: 400,
    },
    sketch: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    title: {
        backgroundColor: titleColor || Palette.primary.main,
        '&:hover': {
            backgroundColor: titleColor || Palette.primary.main,
        },
    },
});
export function AdvancedReportSettingsPage() {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const self = useSelf();
    const { updateSettings } = useSelfActions();
    const { data: org } = useOrgFindById({ variables: { id: self.organisationId } });
    const myCss = useCss(makeMyClasses, [(_a = org === null || org === void 0 ? void 0 : org.settings) === null || _a === void 0 ? void 0 : _a.titleColor]);
    const [showSketchPicker, setShowSketchPicker] = useState(false);
    const updateOrg = useOrgUpdate();
    const [, setUploading] = useState(false);
    const updateOrganisation = useCallback(patch => {
        if (!(org === null || org === void 0 ? void 0 : org.settings))
            return;
        updateOrg({
            id: org === null || org === void 0 ? void 0 : org.id,
            patch: {
                settings: {
                    ...patch,
                },
            },
        }, {
            optimisticResponse: {
                __typename: 'Mutation',
                updateOrg: {
                    __typename: 'Organisation',
                    ...org,
                    ...{ settings: { ...org === null || org === void 0 ? void 0 : org.settings, ...patch } },
                },
            },
        })
            .then(updatedOrg => {
            updateSettings(updatedOrg === null || updatedOrg === void 0 ? void 0 : updatedOrg.settings);
        })
            .catch(doNothing);
    }, [org, updateSettings, updateOrg]);
    const handleImagesChange = useCallback((photos) => {
        var _a;
        const patch = {
            logoId: photos.length ? (_a = photos[0]) === null || _a === void 0 ? void 0 : _a.fileName : '',
        };
        updateOrganisation(patch);
    }, [updateOrganisation]);
    const { deleteImage, addImage, handleAccept, handleCancel, hideEditor, resolvedImages } = useManyImages({
        photos: (_b = org === null || org === void 0 ? void 0 : org.settings) === null || _b === void 0 ? void 0 : _b.logoId,
        onImagesChange: handleImagesChange,
    });
    const handleChangeColor = useCallback(color => {
        const patch = {
            titleColor: color === null || color === void 0 ? void 0 : color.hex,
        };
        updateOrganisation(patch);
    }, [updateOrganisation]);
    const handleCancelEditor = useCallback(() => {
        handleCancel();
    }, [handleCancel]);
    const toggleSketchPicker = useCallback(() => {
        setShowSketchPicker(true);
    }, []);
    const handleCloseColorPicker = useCallback(() => {
        setShowSketchPicker(false);
    }, []);
    const len = ((_c = resolvedImages === null || resolvedImages === void 0 ? void 0 : resolvedImages.length) !== null && _c !== void 0 ? _c : 0) - 1;
    return (React.createElement(AppPage, null,
        React.createElement(Vertical, { gutter: true, padding: true, style: { overflow: 'auto', maxWidth: 700 } },
            React.createElement(Text, { variant: "h2" }, i18n.t('report-logo')),
            React.createElement(Card, { padding: true },
                React.createElement(Pane, { shrink: true, padding: 4 }, resolvedImages.length ? (React.createElement(ImageViewer, { resolvedImages: resolvedImages, size: 100, onDeleteImage: deleteImage, editable: true })) : (React.createElement("img", { alt: "Logo", src: opsbaseLogo, width: 80, height: 80 }))),
                React.createElement(Pane, { shrink: true, padding: 4 },
                    React.createElement(ImageUploadButton, { onUploading: setUploading, onUpload: addImage })),
                !hideEditor && (React.createElement(Pane, { shrink: true, padding: 4 },
                    React.createElement(ImageEditorDialog, { dataUrl: (_d = resolvedImages[len]) === null || _d === void 0 ? void 0 : _d.imageData, onCancel: handleCancelEditor, onAccept: handleAccept })))),
            React.createElement(Pane, { padding: 4 }),
            React.createElement(Text, { variant: "h2" }, i18n.t('report-title-color')),
            React.createElement(Card, { padding: true },
                React.createElement(Pane, { shrink: true, padding: 4 },
                    React.createElement(Button, { color: ((_e = org === null || org === void 0 ? void 0 : org.settings) === null || _e === void 0 ? void 0 : _e.titleColor)
                            ? {
                                main: (_f = org === null || org === void 0 ? void 0 : org.settings) === null || _f === void 0 ? void 0 : _f.titleColor,
                                dark: (_g = org === null || org === void 0 ? void 0 : org.settings) === null || _g === void 0 ? void 0 : _g.titleColor,
                                contrastText: Palette.White.main,
                            }
                            : Palette.primary, onClick: toggleSketchPicker, label: i18n.t('select-color') }))),
            !!showSketchPicker && (React.createElement("div", { style: { position: 'absolute', zIndex: 2 } },
                React.createElement("div", { className: myCss.sketch, onClick: handleCloseColorPicker }),
                React.createElement(SketchPicker, { color: ((_h = org === null || org === void 0 ? void 0 : org.settings) === null || _h === void 0 ? void 0 : _h.titleColor) || Palette.primary.main, onChangeComplete: handleChangeColor }))))));
}
export default AdvancedReportSettingsPage;
